// src/pages/Instituicoes/VisualizarInstituicaoPage.jsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Button
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  AccountCircle as AccountCircleIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Storage as StorageIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import SliderInstituicao from '../../components/Instituicoes/SliderInstituicao';
import ConfiguracaoRepositorio from '../../components/Instituicoes/ConfiguracaoRepositorio';
import GestaoModulos from '../../components/Instituicoes/GestaoModulos';
import DestaquesInstituicao from '../../components/Instituicoes/DestaquesInstituicao';
import ListaPerfis from '../../components/Instituicoes/ListaPerfis';
import PostsList from '../../components/Instituicoes/PostsList';
import NewsletterForm from '../../components/Instituicoes/NewsletterForm';
import { obterInstituicao } from '../../services/instituicaoService';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

/**
 * Função para determinar a cor do Chip com base no tipo de perfil.
 * @param {string} tipo - Tipo do perfil.
 * @returns {string} - Cor correspondente para o Chip.
 */
const getPerfilColor = (tipo) => {
  switch (tipo.toUpperCase()) {
    case 'GESTAO':
      return 'primary'; // Cor primária para perfis de gestão
    case 'ADMIN':
      return 'secondary'; // Cor secundária para perfis administrativos
    case 'USUARIO':
      return 'default'; // Cor padrão para outros perfis
    default:
      return 'default';
  }
};

/**
 * Função para determinar o ícone do Chip com base no tipo de perfil.
 * @param {string} tipo - Tipo do perfil.
 * @returns {JSX.Element} - Ícone correspondente para o Chip.
 */
const getPerfilIcon = (tipo) => {
  switch (tipo.toUpperCase()) {
    case 'GESTAO':
      return <SupervisorAccountIcon />;
    case 'ADMIN':
      return <AccountCircleIcon />;
    case 'USUARIO':
      return <AccountCircleIcon />;
    default:
      return <AccountCircleIcon />;
  }
};

const VisualizarInstituicaoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtém o usuário autenticado

  const [instituicao, setInstituicao] = useState(null);
  const [loadingInstituicao, setLoadingInstituicao] = useState(true);
  const [loadingPerfis, setLoadingPerfis] = useState(true);
  const [error, setError] = useState(null);

  // Estados para gestão de perfis e abas
  const [tabValue, setTabValue] = useState(0);
  const [perfis, setPerfis] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingInstituicao(true);
      setLoadingPerfis(true);
      try {
        const [instituicaoData, perfisData] = await Promise.all([
          obterInstituicao(id),
          api.get(`/perfis-usuario/instituicao/${id}`).then(res => res.data)
        ]);
        setInstituicao(instituicaoData);
        setPerfis(perfisData);
      } catch (err) {
        console.error('Erro ao carregar instituição ou perfis:', err);
        setError('Não foi possível carregar os dados da instituição.');
      } finally {
        setLoadingInstituicao(false);
        setLoadingPerfis(false);
      }
    };

    fetchData();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Função para excluir instituição
  const handleExcluir = async () => {
    if (window.confirm('Tem certeza que deseja excluir esta instituição?')) {
      try {
        await api.delete(`/instituicoes/${id}`);
        navigate('/instituicoes');
      } catch (err) {
        console.error('Erro ao excluir instituição:', err);
        setError('Erro ao excluir a instituição.');
      }
    }
  };

  // Função para atualizar perfis após modificações
  const atualizarPerfis = async () => {
    setLoadingPerfis(true);
    try {
      const perfisData = await api.get(`/perfis-usuario/instituicao/${id}`).then(res => res.data);
      setPerfis(perfisData);
    } catch (err) {
      console.error('Erro ao atualizar perfis:', err);
      setError('Erro ao atualizar perfis.');
    } finally {
      setLoadingPerfis(false);
    }
  };

  if (loadingInstituicao) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (!instituicao) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Typography variant="h6">Instituição não encontrada</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Grid container spacing={3}>
          {/* Cabeçalho com informações básicas */}
          <Grid item xs={12}>
            <Paper elevation={2}>
              <Box p={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    {instituicao.logo ? (
                      <Avatar 
                        src={instituicao.logo} 
                        alt={instituicao.nome}
                        sx={{ width: 80, height: 80 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                        <SchoolIcon fontSize="large" />
                      </Avatar>
                    )}
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h4" gutterBottom>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SchoolIcon sx={{ mr: 1, fontSize: 40 }} />
                        {instituicao.nome}
                      </Box>
                    </Typography>
                    <Chip 
                      label={instituicao.tipoInstituicao}
                      color="primary"
                      variant="outlined"
                      size="small"
                      icon={<SchoolIcon />}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body1" paragraph>
                  {instituicao.descricao}
                </Typography>

                <Grid container spacing={2}>
                  {instituicao.website && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Website:</strong>{' '}
                        <a href={instituicao.website} target="_blank" rel="noopener noreferrer">
                          {instituicao.website}
                        </a>
                      </Typography>
                    </Grid>
                  )}
                  {instituicao.email && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Email:</strong> {instituicao.email}
                      </Typography>
                    </Grid>
                  )}
                  {instituicao.telefone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Telefone:</strong> {instituicao.telefone}
                      </Typography>
                    </Grid>
                  )}
                  {instituicao.endereco && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Endereço:</strong> {instituicao.endereco}
                      </Typography>
                    </Grid>
                  )}
                  {instituicao.temTransportePublico !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Transporte Público:</strong>{' '}
                        <Chip 
                          label={instituicao.temTransportePublico ? 'Disponível' : 'Não Disponível'} 
                          color={instituicao.temTransportePublico ? 'success' : 'default'} 
                          size="small"
                        />
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Botões de Ação */}
                {(user && (user.tipo === 'admin' || (user.tipo === 'instituicao' && user.instituicaoId === parseInt(id)))) && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate(`/instituicoes/editar/${id}`)}
                      sx={{ mr: 1 }}
                    >
                      Editar
                    </Button>
                    <Button variant="contained" color="error" onClick={handleExcluir}>
                      Excluir
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Divider para separar seções */}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Seção de Perfis com Tabs */}
          <Grid item xs={12}>
            <Paper elevation={2}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                <Tab label="Informações" />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PeopleIcon sx={{ mr: 1 }} />
                      Perfis ({perfis.length})
                    </Box>
                  } 
                />
                <Tab label="Posts" />
                <Tab label="Destaques" />
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}><StorageIcon sx={{ mr: 0.5, fontSize: 18 }} />Repositório</Box>} />
                <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ExtensionIcon sx={{ mr: 0.5, fontSize: 18 }} />Módulos</Box>} />
              </Tabs>

              <Box sx={{ p: 3 }}>
                {/* Conteúdo das Tabs */}
                {tabValue === 0 && (
                  <Grid container spacing={2}>
                    {/* Informações Adicionais */}
                    <Grid item xs={12}>
                      <Typography variant="h6">Detalhes Adicionais</Typography>
                      {/* Adicione mais informações conforme necessário */}
                    </Grid>
                  </Grid>
                )}

                {tabValue === 1 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                      <Typography variant="h6">
                        Perfis da Instituição
                      </Typography>
                      {(user?.tipo === 'admin' || (user?.tipo === 'instituicao' && user?.instituicaoId === parseInt(id))) && (
                        <Button
                          variant="contained"
                          startIcon={<AccountCircleIcon />}
                          onClick={() => navigate(`/instituicoes/${id}/perfis/adicionar`)}
                        >
                          Adicionar Perfil
                        </Button>
                      )}
                    </Box>

                    {loadingPerfis ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <CircularProgress />
                      </Box>
                    ) : perfis.length > 0 ? (
                      <ListaPerfis 
                        perfis={perfis}
                        instituicaoId={id}
                        onUpdate={atualizarPerfis}
                        getPerfilColor={getPerfilColor} // Passando a função como prop
                        getPerfilIcon={getPerfilIcon}   // Passando a função como prop
                      />
                    ) : (
                      <Typography>Nenhum perfil encontrado.</Typography>
                    )}
                  </Box>
                )}

                {tabValue === 2 && (
                  <Box>
                    <PostsList instituicaoId={id} />
                  </Box>
                )}

                {tabValue === 3 && (
                  <Box>
                    <DestaquesInstituicao instituicaoId={id} modo="publico" />
                  </Box>
                )}

                {tabValue === 4 && (
                  <ConfiguracaoRepositorio instituicaoId={id} instituicao={instituicao} />
                )}

                {tabValue === 5 && (
                  <GestaoModulos instituicaoId={id} />
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Slider da Instituição */}
          <Grid item xs={12}>
            <Paper elevation={2}>
              <Box p={3}>
                <Typography variant="h5" gutterBottom>
                  Galeria
                </Typography>
                <SliderInstituicao 
                  instituicaoId={id} 
                  modo="publico" 
                />
              </Box>
            </Paper>
          </Grid>

          {/* Destaques da Instituição */}
          <Grid item xs={12}>
            <Paper elevation={2}>
              <Box p={3}>
                <Typography variant="h5" gutterBottom>
                  Destaques
                </Typography>
                <DestaquesInstituicao 
                  instituicaoId={id} 
                  modo="publico" 
                />
              </Box>
            </Paper>
          </Grid>

          {/* Newsletter Form */}
          <Grid item xs={12}>
            <Paper elevation={2}>
              <Box p={3}>
                <Typography variant="h5" gutterBottom>
                  Inscreva-se na nossa Newsletter
                </Typography>
                <NewsletterForm instituicaoId={id} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default VisualizarInstituicaoPage;
